import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import Box from 'carbon-react/lib/components/box';
import { FilterableSelect, Option } from 'carbon-react/lib/components/select';

import { ReportTemplateList } from '@futrli-sdk/predict';

import * as styles from './report-form-screen.scss';
import { REPORT_FORM_FIELD_IDS } from '../constants';
import { CreateReportSidebarMode } from '../create-report-sidebar';

export interface FutrliTemplateSelectProps {
  templates: ReportTemplateList[];
  selectedTemplateId: string;
  onSelectedTemplateIdChange: (value: string) => void;
  error?: string;
  onBlur?: () => void;
  mode?: CreateReportSidebarMode;
}

export const FutrliTemplateSelect = ({
  templates,
  selectedTemplateId,
  onSelectedTemplateIdChange,
  error,
  onBlur,
  mode = 'report'
}: FutrliTemplateSelectProps) => {
  const intl = useIntl();
  const isDashboard = mode === 'dashboard';

  const portraitTemplates = useMemo(() => templates.filter(t => t.orientation === 'portrait'), [templates]);
  const landscapeTemplates = useMemo(() => templates.filter(t => t.orientation === 'landscape'), [templates]);

  return (
    <FilterableSelect
      id={REPORT_FORM_FIELD_IDS.FUTRLI_TEMPLATE_SELECT}
      name={REPORT_FORM_FIELD_IDS.FUTRLI_TEMPLATE_SELECT}
      label={intl.formatMessage({
        id: 'reporting.select_a_template',
        defaultMessage: 'Select a template'
      })}
      placeholder={intl.formatMessage({
        id: 'reporting.type_to_search_templates',
        defaultMessage: 'Type to search templates'
      })}
      inputHint={intl.formatMessage({
        id: 'reporting.type_to_search_templates',
        defaultMessage: 'Type to search templates'
      })}
      value={selectedTemplateId}
      onChange={e => onSelectedTemplateIdChange(e.target.value as string)}
      mb={0}
      listMaxHeight={340}
      error={error}
      onBlur={onBlur}
      required
    >
      {isDashboard
        ? templates.map(t => <Option key={t.id} value={t.id} text={t.name} />)
        : [
            ...(portraitTemplates.length > 0
              ? [
                  <Option
                    key="portrait-header"
                    text={intl.formatMessage({
                      id: 'reporting.portrait_templates',
                      defaultMessage: 'Portrait Templates'
                    })}
                    disabled
                    className={styles.disabledOption}
                  />,
                  ...portraitTemplates.map(t => <Option key={t.id} value={t.id} text={t.name} />)
                ]
              : []),
            ...(portraitTemplates.length > 0 && landscapeTemplates.length > 0
              ? [
                  <Option key="separator" text="" disabled>
                    <Box width="100%" height="1px" backgroundColor="#E6EBED" />
                  </Option>
                ]
              : []),
            ...(landscapeTemplates.length > 0
              ? [
                  <Option
                    key="landscape-header"
                    text={intl.formatMessage({
                      id: 'reporting.landscape_templates',
                      defaultMessage: 'Landscape Templates'
                    })}
                    disabled
                    className={styles.disabledOption}
                  />,
                  ...landscapeTemplates.map(t => <Option key={t.id} value={t.id} text={t.name} />)
                ]
              : [])
          ]}
    </FilterableSelect>
  );
};
